import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';

const BACKEND_URL = window.location.origin;

const AdminAboutPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/');
      return;
    }
    fetchContent();
  }, [user, navigate]);

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/about/content`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
      alert('Failed to load content');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/admin/about/content`, content, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      alert('Content saved successfully!');
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Failed to save content');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${BACKEND_URL}/api/admin/about/upload-media`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });

      const fileUrl = response.data.url;
      
      // Add URL to appropriate array
      setContent(prev => ({
        ...prev,
        [fieldName]: [...(prev[fieldName] || []), fileUrl]
      }));

      alert('File uploaded successfully!');
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const addTimelineEvent = () => {
    setContent(prev => ({
      ...prev,
      timeline: [...(prev?.timeline || []), { year: '', event: '' }]
    }));
  };

  const updateTimelineEvent = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      timeline: prev.timeline.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeTimelineEvent = (index) => {
    setContent(prev => ({
      ...prev,
      timeline: prev.timeline.filter((_, i) => i !== index)
    }));
  };

  const addTestimonial = () => {
    setContent(prev => ({
      ...prev,
      testimonials: [...(prev?.testimonials || []), { quote: '', author: '', image: '' }]
    }));
  };

  const updateTestimonial = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      testimonials: prev.testimonials.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeTestimonial = (index) => {
    setContent(prev => ({
      ...prev,
      testimonials: prev.testimonials.filter((_, i) => i !== index)
    }));
  };

  const addValue = () => {
    setContent(prev => ({
      ...prev,
      values: [...(prev?.values || []), { icon: '', title: '', desc: '' }]
    }));
  };

  const updateValue = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      values: prev.values.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeValue = (index) => {
    setContent(prev => ({
      ...prev,
      values: prev.values.filter((_, i) => i !== index)
    }));
  };

  const removeImage = (fieldName, index) => {
    setContent(prev => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, i) => i !== index)
    }));
  };

  if (!content) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900">Edit About Page</h1>
          <div className="flex gap-4">
            <Button onClick={() => navigate('/about')} variant="outline">
              Preview Page
            </Button>
            <Button onClick={handleSave} disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Hero Section */}
        <Card className="p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4 text-orange-500">Hero Section</h2>
          <div className="space-y-4">
            <div>
              <Label>Hero Title</Label>
              <Input
                value={content.hero_title}
                onChange={(e) => setContent({...content, hero_title: e.target.value})}
                placeholder="Welcome to NLV"
              />
            </div>
            <div>
              <Label>Hero Subtitle</Label>
              <Input
                value={content.hero_subtitle}
                onChange={(e) => setContent({...content, hero_subtitle: e.target.value})}
                placeholder="Amsterdam's most dynamic volleyball community"
              />
            </div>
          </div>
        </Card>

        {/* Who We Are Section */}
        <Card className="p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4 text-orange-500">Who We Are</h2>
          <div className="space-y-4">
            <div>
              <Label>Text Content</Label>
              <Textarea
                value={content.who_we_are_text}
                onChange={(e) => setContent({...content, who_we_are_text: e.target.value})}
                rows={6}
              />
            </div>
            <div>
              <Label>Images (Photo Collage)</Label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(e, 'who_we_are_images')}
                disabled={uploading}
                className="mb-2"
              />
              <div className="grid grid-cols-3 gap-4 mt-2">
                {content.who_we_are_images?.map((url, index) => (
                  <div key={index} className="relative">
                    <img src={`${BACKEND_URL}${url}`} alt="" className="w-full h-32 object-cover rounded" />
                    <button
                      onClick={() => removeImage('who_we_are_images', index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Our Story Section */}
        <Card className="p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4 text-orange-500">Our Story</h2>
          <div className="space-y-4">
            <div>
              <Label>Story Text</Label>
              <Textarea
                value={content.story_text}
                onChange={(e) => setContent({...content, story_text: e.target.value})}
                rows={3}
              />
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Timeline Events</Label>
                <Button onClick={addTimelineEvent} size="sm">+ Add Event</Button>
              </div>
              {content.timeline?.map((event, index) => (
                <div key={index} className="flex gap-4 mb-3 items-start">
                  <Input
                    value={event.year}
                    onChange={(e) => updateTimelineEvent(index, 'year', e.target.value)}
                    placeholder="Year"
                    className="w-32"
                  />
                  <Input
                    value={event.event}
                    onChange={(e) => updateTimelineEvent(index, 'event', e.target.value)}
                    placeholder="Event description"
                    className="flex-1"
                  />
                  <Button onClick={() => removeTimelineEvent(index)} variant="destructive" size="sm">
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Testimonials Section */}
        <Card className="p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4 text-orange-500">Community Vibes</h2>
          <Button onClick={addTestimonial} size="sm" className="mb-4">+ Add Testimonial</Button>
          {content.testimonials?.map((test, index) => (
            <div key={index} className="border p-4 rounded mb-3">
              <div className="space-y-3">
                <Input
                  value={test.quote}
                  onChange={(e) => updateTestimonial(index, 'quote', e.target.value)}
                  placeholder="Quote"
                />
                <Input
                  value={test.author}
                  onChange={(e) => updateTestimonial(index, 'author', e.target.value)}
                  placeholder="Author name"
                />
                <div>
                  <Label>Upload Image for Testimonial</Label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      setUploading(true);
                      try {
                        const token = localStorage.getItem('token');
                        const formData = new FormData();
                        formData.append('file', file);
                        const response = await axios.post(`${BACKEND_URL}/api/admin/about/upload-media`, formData, {
                          headers: { 
                            'Content-Type': 'multipart/form-data',
                            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                          }
                        });
                        updateTestimonial(index, 'image', response.data.url);
                        alert('Image uploaded successfully!');
                      } catch (error) {
                        console.error('Error uploading:', error);
                        alert('Failed to upload image');
                      } finally {
                        setUploading(false);
                      }
                    }}
                    disabled={uploading}
                    className="mt-2"
                  />
                  {test.image && test.image.startsWith('/') && (
                    <div className="mt-2">
                      <img src={`${BACKEND_URL}${test.image}`} alt="Preview" className="w-32 h-32 object-cover rounded" />
                    </div>
                  )}
                </div>
                <Button onClick={() => removeTestimonial(index)} variant="destructive" size="sm">
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </Card>

        {/* Values Section */}
        <Card className="p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4 text-orange-500">Our Values</h2>
          <Button onClick={addValue} size="sm" className="mb-4">+ Add Value</Button>
          {content.values?.map((val, index) => (
            <div key={index} className="border p-4 rounded mb-3">
              <div className="space-y-3">
                <Input
                  value={val.icon}
                  onChange={(e) => updateValue(index, 'icon', e.target.value)}
                  placeholder="Emoji (e.g., 🏐)"
                  className="w-24"
                />
                <Input
                  value={val.title}
                  onChange={(e) => updateValue(index, 'title', e.target.value)}
                  placeholder="Title"
                />
                <Input
                  value={val.desc}
                  onChange={(e) => updateValue(index, 'desc', e.target.value)}
                  placeholder="Description"
                />
                <Button onClick={() => removeValue(index)} variant="destructive" size="sm">
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </Card>

        {/* Gallery Section */}
        <Card className="p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4 text-orange-500">Gallery</h2>
          <input
            type="file"
            accept="image/*,video/*"
            onChange={(e) => handleFileUpload(e, 'gallery_images')}
            disabled={uploading}
            className="mb-4"
          />
          <div className="grid grid-cols-3 gap-4">
            {content.gallery_images?.map((url, index) => (
              <div key={index} className="relative">
                {url.includes('.mp4') || url.includes('.webm') ? (
                  <video src={`${BACKEND_URL}${url}`} className="w-full h-32 object-cover rounded" controls />
                ) : (
                  <img src={`${BACKEND_URL}${url}`} alt="" className="w-full h-32 object-cover rounded" />
                )}
                <button
                  onClick={() => removeImage('gallery_images', index)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </Card>

        {/* Contact Section */}
        <Card className="p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4 text-orange-500">Stay Connected</h2>
          <div className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input
                value={content.contact_email}
                onChange={(e) => setContent({...content, contact_email: e.target.value})}
              />
            </div>
            <div>
              <Label>Instagram Handle</Label>
              <Input
                value={content.contact_instagram}
                onChange={(e) => setContent({...content, contact_instagram: e.target.value})}
              />
            </div>
            <div>
              <Label>Instagram URL</Label>
              <Input
                value={content.contact_instagram_url}
                onChange={(e) => setContent({...content, contact_instagram_url: e.target.value})}
              />
            </div>
          </div>
        </Card>

        <div className="flex justify-end gap-4">
          <Button onClick={() => navigate('/admin')} variant="outline">
            Back to Admin
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminAboutPage;
